import React, { Fragment, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams, useSearchParams } from "react-router-dom"

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import ExitToAppIcon from "@mui/icons-material/ExitToApp"
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined"
import { CircularProgress, Grid } from "@mui/material"
import { Button, Tab, Tabs } from "@synapse-analytics/synapse-ui"
import type { AxiosResponse } from "axios"

import { SideBar } from "../components/UI/SideBar"
import { KonanAvatarComponent } from "../components/core/KonanAvatar"
import { KonanSubHeader } from "../components/core/KonanSubHeader"
import { KonanTabsHeader } from "../components/core/KonanTabsHeader"
import type { FilterOptions } from "../components/filters/MenuFilter"
import { KonanEmptyState, KonanEmptyStateFilter } from "../components/utility/KonanEmptyState"
import { DataFileCard, DataFileLoadingCard } from "../features/DataFiles"
import { DataFileUpload } from "../features/DataFiles/DataFileUpload"
import { ExportJobCard, ExportJobsLoadingCard } from "../features/ExportJobs"
import { TrainingData } from "../features/TrainingData/TrainingData"
import { useDebounce } from "../hooks/useDebounce"
import { useHandlePageQueryParam } from "../hooks/useHandlePageQueryParam"
import { KonanAPI } from "../services/KonanAPI"
import { queryClient } from "../store/QueryClientStore"
import type { RootState } from "../store/ReduxStore"
import type { DataFileGroupList } from "../types/generated/api/DataFileGroupList"
import type { ExportJobList } from "../types/generated/api/ExportJobList"
import type { PaginatedDownloadExportJobList } from "../types/generated/api/PaginatedDownloadExportJobList"
import type { PaginatedExportJobListList } from "../types/generated/api/PaginatedExportJobListList"
import { extractPageFromBackEndPaginationLink } from "../utils/genericHelpers"
import { FilesView } from "./FilesView"

enum FilterMap {
  "success" = "SUCCEEDED",
  "failed" = '["FAILED", "CANCELLED"]',
  "inProgress" = '["RUNNING", "PENDING"]',
}

/**
 * Export Jobs page
 *
 * @returns {React.ReactElement}
 */
function ExportJobs(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastExportJobRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isExportJobsLoading && !isExportJobsFetching && !isFetchingNextPage && fetchNextPage()
    },
  })

  const { t } = useTranslation("datafiles")

  // SSF states
  const [searchValue, setSearchValue] = useState<string>("")
  const [sortValue, setSortValue] = useState<string>("Most Recent")
  const [filterValue, setFilterValue] = useState<string>("success")

  // Side bar states
  const [exportJobName, setExportJobName] = useState<string | null>(null)
  const [isSideBarOpen, setIsSideBarOpen] = useState<boolean>(false)

  // fetch export jobs
  const {
    data: ExportJobs,
    isLoading: isExportJobsLoading,
    isFetching: isExportJobsFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery<AxiosResponse<PaginatedExportJobListList>>(
    ["data-files", projectId, filterValue, sortValue],
    ({ pageParam = 1 }) =>
      KonanAPI.listExportJobs({
        project_uuid: projectId as string,
        search: searchValue,
        status:
          filterValue === "success"
            ? FilterMap[filterValue]
            : JSON.parse(FilterMap[filterValue as keyof typeof FilterMap]),
        page: pageParam,
        page_size: 20,
        ordering: sortValue === "Most Recent" ? "-created_at" : "name",
      }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId,
    },
  )

  // list download logs for export job
  const {
    refetch: refetchDownloadLogs,
    isLoading: isDownloadLogsLoading,
    data: downloadLogsData,
  } = useQuery<AxiosResponse<PaginatedDownloadExportJobList>>(
    ["retrieve-export-downloads", exportJobName],
    () =>
      KonanAPI.retrieveExportJobsDownloadLogs({ project_uuid: projectId as string, search: exportJobName as string }),
    {
      enabled: false,
    },
  )

  const adjustedDataFiles = useMemo(() => {
    const reducedDataFiles = ExportJobs?.pages.reduce((accumulator, page) => {
      return accumulator.concat(page.data.results as ExportJobList[])
    }, [] as ExportJobList[])

    return reducedDataFiles?.length && reducedDataFiles?.length > 0 ? reducedDataFiles : []
  }, [ExportJobs?.pages])

  const debouncedOnChange = useDebounce((): void => {
    refetch()
  }, 400)

  return (
    <Grid container item>
      {isExportJobsLoading ||
      isExportJobsFetching ||
      (!isExportJobsLoading &&
        ((ExportJobs?.pages[0]?.data?.count ?? 0) > 0 || !!searchValue || !!filterValue.length)) ? (
        <Fragment>
          {/* SSF */}
          <Grid container item mt={3}>
            <KonanTabsHeader
              title={t("headerTitle")}
              containerType={"Data file"}
              setTabValue={setFilterValue}
              value={t(filterValue)}
              setSearchValue={(value) => {
                setSearchValue(value)
                debouncedOnChange()
              }}
              searchValue={searchValue}
              sortValue={sortValue}
              sortOptions={[
                {
                  label: t("sortOptions.mostRecent"),
                  value: "Most Recent",
                },
                { label: t("sortOptions.name"), value: "Name" },
              ]}
              setSortValue={setSortValue}
            />
          </Grid>

          {/* Data files */}
          <Grid item xs={12}>
            {isExportJobsLoading ? (
              <Grid container spacing={2}>
                {[1, 2, 3].map((item) => (
                  <Grid item xs={12} md={6} lg={4} xl={3} key={item}>
                    <ExportJobsLoadingCard />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid container item xs={12} spacing={2}>
                {adjustedDataFiles.length > 0 ? (
                  <Fragment>
                    {adjustedDataFiles?.map((job: ExportJobList, index: number) => (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={4}
                        xl={3}
                        key={job.uuid}
                        ref={hasNextPage && index === adjustedDataFiles.length - 1 ? lastExportJobRef : undefined}
                      >
                        <ExportJobCard
                          exportJob={job}
                          openDataFileHistory={(name: string) => {
                            setIsSideBarOpen(true)
                            setExportJobName(name)

                            setTimeout(() => {
                              refetchDownloadLogs()
                            }, 15)
                          }}
                        />
                      </Grid>
                    ))}
                    {hasNextPage &&
                      [1, 2, 3].map((item: number) => (
                        <Grid item xs={12} md={6} lg={4} xl={3} key={item}>
                          <ExportJobsLoadingCard />
                        </Grid>
                      ))}
                  </Fragment>
                ) : searchValue ? (
                  <KonanEmptyState
                    title={t("searchEmptyState.title", {
                      searchValue: searchValue?.charAt(0)?.toUpperCase() + searchValue?.slice(1)?.toLowerCase(),
                    })}
                    subTitle={t("searchEmptyState.subtitle")}
                  />
                ) : (
                  filterValue?.length > 0 && (
                    <KonanEmptyStateFilter
                      title={[
                        filterValue === "inProgress"
                          ? "In progress"
                          : filterValue?.charAt(0)?.toUpperCase() + filterValue?.slice(1)?.toLowerCase(),
                      ]}
                    />
                  )
                )}
              </Grid>
            )}
          </Grid>
        </Fragment>
      ) : (
        <KonanEmptyState title={t("emptyState.title")} subTitle={t("emptyState.subtitle")} />
      )}

      <SideBar
        isOpen={isSideBarOpen}
        title={t("sidebarTitle")}
        onClose={() => {
          setIsSideBarOpen(false)
          setExportJobName(null)
          queryClient.removeQueries({ queryKey: "retrieve-export-downloads" })
        }}
      >
        {isDownloadLogsLoading ? (
          <Grid container direction="row" justifyContent="center" alignItems="center" style={{ minHeight: "100%" }}>
            <CircularProgress />
          </Grid>
        ) : (
          <Grid container item xs={12} spacing={1.5}>
            {(downloadLogsData?.data.results ?? []).length > 0 ? (
              downloadLogsData?.data.results?.map((log) => (
                <Grid item xs={12} key={log.export_job.uuid}>
                  <KonanAvatarComponent
                    createdAt={log.downloaded_at}
                    createdBy={log.downloaded_by}
                    title={log.downloaded_by}
                    noWrap={false}
                  />
                </Grid>
              ))
            ) : (
              <Grid container item xs={12} display={"flex"} justifyContent={"center"} alignContent={"center"}>
                <KonanEmptyState
                  title={t("nodownloadHistory", {
                    exportJobName: exportJobName,
                  })}
                />
              </Grid>
            )}
          </Grid>
        )}
      </SideBar>
    </Grid>
  )
}

function DataFilesListing(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastDataFileRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isDataFilesLoading && !isDataFilesFetching && !isFetchingNextPage && fetchNextPage()
    },
  })

  const { t } = useTranslation("datafiles")

  const DataFilesFilterMap = {
    GENERIC: "Generic",
    PREDICTIONS: "Predictions",
    CONDITION_LIST: "Condition list",
    ACTUAL_DECISIONS: "Actual decisions",
  } as const

  const [searchValue, setSearchValue] = useState<string>("")
  const [sortValue, setSortValue] = useState<string>("Most Recent")
  const [filterValue, setFilterValue] = useState<FilterOptions>({ "File type": [] })

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false)

  // fetch Data files
  const {
    data: dataFiles,
    isLoading: isDataFilesLoading,
    isFetching: isDataFilesFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    ["data-files", projectId, searchValue, sortValue, filterValue],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchDataFiles({
        project_uuid: projectId as string,
        page: pageParam,
        pageSize: 20,
        search: searchValue,
        ordering: sortValue === "Name" ? "name" : "-created_at",
        type: filterValue["File type"]?.length
          ? filterValue["File type"].map(
              (filter) => Object.entries(DataFilesFilterMap).find(([_, value]) => value === filter)?.[0] as string,
            )
          : ["GENERIC", "PREDICTIONS", "CONDITION_LIST", "ACTUAL_DECISIONS"],
      }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      select: ({ pageParams, pages }) => ({
        pageParams,
        pages: pages.reduce(
          (accumulator, page) => accumulator.concat(page.data.results as Array<DataFileGroupList>),
          [] as Array<DataFileGroupList>,
        ),
      }),
      enabled: !!projectId,
    },
  )

  return (
    <Grid container item>
      {isUploadDialogOpen && <DataFileUpload onClose={() => setIsUploadDialogOpen(false)} open={isUploadDialogOpen} />}

      {isDataFilesLoading ||
      isDataFilesFetching ||
      (!isDataFilesLoading &&
        ((dataFiles?.pages.length ?? 0) > 0 || !!searchValue || !!filterValue["File type"]?.length)) ? (
        <Fragment>
          {/* SSF */}
          <Grid item xs={12} mb={2}>
            <KonanSubHeader
              searchValue={searchValue}
              isDebouncedInput
              setSearchValue={setSearchValue}
              sortValue={sortValue}
              setSortValue={setSortValue}
              filterValue={filterValue}
              setFilterValue={setFilterValue}
              filterOptions={{
                "File type": Object.values(DataFilesFilterMap),
              }}
              containerType="File"
              sortOptions={[
                {
                  label: t("sortOptions.mostRecent"),
                  value: "Most Recent",
                },
                { label: t("sortOptions.name"), value: "Name" },
              ]}
              useMenuFilter
              actions={[
                <Button key={"upload-btn"} variant="primary" onClick={() => setIsUploadDialogOpen(true)}>
                  <AddCircleOutlineOutlinedIcon fontSize="small" />
                  {t("dataFilesListing.actions.uploadFile")}
                </Button>,
              ]}
            />
          </Grid>

          {/* Data files */}
          <Grid container item xs={12} spacing={2}>
            {isDataFilesLoading ||
            isDataFilesFetching ||
            isFetchingNextPage ||
            (dataFiles?.pages && dataFiles?.pages.length > 0) ? (
              <Fragment>
                {dataFiles?.pages?.map((file: DataFileGroupList, index: number) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    xl={3}
                    key={file.uuid}
                    ref={hasNextPage && index === dataFiles?.pages.length - 1 ? lastDataFileRef : undefined}
                  >
                    <DataFileCard dataFile={file} />
                  </Grid>
                ))}
                {(isDataFilesLoading || isDataFilesFetching || isFetchingNextPage) &&
                  [1, 2, 3].map((item) => (
                    <Grid item xs={12} md={6} lg={4} xl={3} key={item}>
                      <DataFileLoadingCard />
                    </Grid>
                  ))}
              </Fragment>
            ) : searchValue ? (
              <KonanEmptyState
                title={t("dataFilesListing.empty.noResults", {
                  value: searchValue.charAt(0).toUpperCase() + searchValue.slice(1).toLowerCase(),
                })}
                subTitle={t("dataFilesListing.empty.adjustSearch")}
              />
            ) : (
              filterValue?.["File type"]?.length &&
              filterValue?.["File type"]?.length > 0 && (
                <KonanEmptyState
                  title={t("dataFilesListing.empty.noResults", {
                    value: filterValue?.["File type"]?.join(", "),
                  })}
                  subTitle={t("dataFilesListing.empty.adjustFilter")}
                />
              )
            )}
          </Grid>
        </Fragment>
      ) : (
        <KonanEmptyState
          title={t("dataFilesListing.empty.noDataFiles")}
          subTitle={t("dataFilesListing.empty.uploadPrompt")}
          buttonText={t("dataFilesListing.actions.uploadFile")}
          setAction={() => setIsUploadDialogOpen(true)}
        />
      )}
    </Grid>
  )
}

/**
 * Data files page
 * home to Konan Model Training
 * Export Jobs, Training Data
 *
 * @return {React.ReactElement}
 */
export function DataFiles(): React.ReactElement {
  const [value, setTabValue] = useHandlePageQueryParam(
    ["Export-data-files", "Training-data", "Data-files"],
    "Data-files",
  )

  const [searchParams] = useSearchParams()

  const { t } = useTranslation("datafiles")

  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const tabs = [
    {
      value: "Data-files",
      label: t("tabs.dataFiles"),
      icon: () => <ExitToAppIcon fontSize="small" />,
      shouldRender: true,
    },
    {
      value: "Export-data-files",
      label: t("tabs.exportJobs"),
      icon: () => <FolderOutlinedIcon fontSize="small" />,
      shouldRender: true,
    },
    {
      value: "Training-data",
      label: t("tabs.trainingData"),
      icon: () => <AssignmentOutlinedIcon fontSize="small" />,
      shouldRender: currentProject?.category !== "workflow",
    },
  ]

  return searchParams.get("file_id") ? (
    <FilesView />
  ) : (
    <Grid container gap={1} p={2}>
      <Grid item xs={12}>
        <Tabs value={value} className="base-tabs">
          {tabs.map((tab) => (
            <Tab
              key={tab.label}
              icon={tab.icon}
              label={tab.label}
              value={tab.value}
              selected={value === tab.value}
              onClick={() => setTabValue(tab.value)}
              size="large"
              style={{ display: !tab.shouldRender ? "none" : "flex" }}
            />
          ))}
        </Tabs>
      </Grid>

      <Grid item xs={12}>
        {value === "Export-data-files" ? (
          <ExportJobs />
        ) : value === "Data-files" ? (
          <DataFilesListing />
        ) : (
          <TrainingData />
        )}
      </Grid>
    </Grid>
  )
}
